import { produce } from 'immer'
import {
  REMOVE_ORGANIZATION_INVITE,
  RemoveOrganizationInviteActions,
} from 'src/app/PeopleInvitedToOrg/redux/peopleInvitedToOrg.actions'
import {
  OrganizationInviteResponse,
  OrganizationsListResponse,
} from 'src/models/organizations.models'
import {
  ABANDON_CREATE_ORGANIZATION,
  ABANDON_EDIT_ORGANIZATION,
  DeleteOrganizationActions,
  EDIT_ORGANIZATION_INIT,
  GET_ORGANIZATION_INTEGRATIONS_SUCCESS,
  GET_ORGANIZATION_INVITE_SUCCESS,
  GET_ORGANIZATION_INVITES_SUCCESS,
  GET_ORGANIZATION_SUCCESS,
  GET_ORGANIZATIONS_SUCCESS,
  GET_USER_ORGANIZATION_INVITES_SUCCESS,
  GetOrganizationActions,
  GetOrganizationIntegrationsActions,
  GetOrganizationInviteActions,
  GetOrganizationInvitesActions,
  GetOrganizationsActions,
  GetOrganizationSuccessAction,
  GetUserOrganizationInvitesActions,
  InviteOrganizationActions,
  PATCH_ORGANIZATIONS_SUCCESS,
  PatchOrganizationsActions,
  POST_ORGANIZATION_FAILURE,
  POST_ORGANIZATION_INIT,
  POST_ORGANIZATION_SUCCESS,
  PostOrganizationActions,
  PUT_ORGANIZATION_FAILURE,
  PUT_ORGANIZATION_INIT,
  PUT_ORGANIZATION_SUCCESS,
  PutOrganizationActions,
} from 'src/redux/actions/data/organizations.actions'
import OrgExpenseReportsReducer, {
  Actions as GetOrgExpenseReportsActions,
  initialState as initialOrgExpenseReportsState,
  OrgExpenseReportsState,
} from 'src/redux/reducers/data/orgExpenseReports.reducer'
import { parseBackendErrors } from 'src/utils/error.utils'

export interface OrganizationInvitesState {
  listResponses: Record<string, OrganizationInviteResponse>
}

export interface OrganizationsState {
  listResponses: Record<string, OrganizationsListResponse>
  totalItems?: number
  isOrganizationBeingEdited: boolean
  patchingOrganizations: boolean
  puttingOrganizations: boolean
  patchOrganizationsTopLevelError?: string
  putOrganizationsTopLevelError?: string
  organizationInvites: OrganizationInvitesState
  puttingOrganizationRole: boolean
  postingOrganization?: boolean
  topLevelError?: string
  integrations: string[]
  reports: OrgExpenseReportsState
}

export const initialInviteState: OrganizationInvitesState = {
  listResponses: {},
}

export const initialState: OrganizationsState = {
  listResponses: {},
  isOrganizationBeingEdited: false,
  patchingOrganizations: false,
  puttingOrganizations: false,
  totalItems: 0,
  organizationInvites: initialInviteState,
  puttingOrganizationRole: false,
  postingOrganization: false,
  integrations: [],
  reports: initialOrgExpenseReportsState,
}

export type Actions =
  | GetOrganizationActions
  | GetOrganizationsActions
  | PatchOrganizationsActions
  | PostOrganizationActions
  | PutOrganizationActions
  | DeleteOrganizationActions
  | InviteOrganizationActions
  | GetUserOrganizationInvitesActions
  | GetOrganizationInviteActions
  | GetOrganizationInvitesActions
  | GetOrganizationSuccessAction
  | GetOrganizationIntegrationsActions
  | RemoveOrganizationInviteActions
  | GetOrgExpenseReportsActions

const OrganizationsReducer = (
  state: OrganizationsState = initialState,
  action: Actions,
): OrganizationsState => {
  const updateOrgWithIntegrationsAndReceiptRules = (
    oldOrg: OrganizationsListResponse,
    newOrg: OrganizationsListResponse,
  ): OrganizationsListResponse => {
    return oldOrg
      ? {
          ...newOrg,
          integrations:
            newOrg['integrations'] !== undefined
              ? newOrg['integrations']
              : oldOrg['integrations'],
        }
      : newOrg
  }

  return {
    ...produce(state, (draft) => {
      switch (action.type) {
        case GET_ORGANIZATIONS_SUCCESS: {
          draft.listResponses = action.payload.organizations.reduce(
            (acc, curr) => ({
              ...acc,
              [curr.id]: updateOrgWithIntegrationsAndReceiptRules(
                draft.listResponses[curr.id],
                curr,
              ),
            }),
            draft.listResponses,
          )
          return draft
        }

        case POST_ORGANIZATION_SUCCESS: {
          draft.listResponses = {
            ...draft.listResponses,
            [action.payload.organization.id]: action.payload.organization,
          }
          draft.postingOrganization = false
          return draft
        }

        case GET_ORGANIZATION_SUCCESS: {
          draft.listResponses = {
            ...draft.listResponses,
            [action.payload.organization.id]: updateOrgWithIntegrationsAndReceiptRules(
              draft.listResponses[action.payload.organization.id],
              action.payload.organization,
            ),
          }
          return draft
        }

        case POST_ORGANIZATION_INIT: {
          draft.topLevelError = undefined
          draft.postingOrganization = true
          return draft
        }

        case ABANDON_CREATE_ORGANIZATION: {
          draft.topLevelError = undefined
          return draft
        }

        case POST_ORGANIZATION_FAILURE: {
          draft.postingOrganization = false
          const errors = parseBackendErrors(action.payload)
          draft.topLevelError = errors?.errorDetailMap
            ? `${Object.keys(errors.errorDetailMap)[0]} ${Object.values(errors.errorDetailMap)[0]}`
            : errors.topLevelError
          return draft
        }

        case PUT_ORGANIZATION_INIT: {
          draft.topLevelError = undefined
          draft.puttingOrganizations = true
          return draft
        }

        case EDIT_ORGANIZATION_INIT: {
          draft.topLevelError = undefined
          draft.isOrganizationBeingEdited = true
          return draft
        }
        case PUT_ORGANIZATION_SUCCESS: {
          draft.listResponses = {
            ...draft.listResponses,
            [action.payload.organization.id]: {
              ...draft.listResponses[action.payload.organization.id],
              ...action.payload.organization,
            },
          }
          draft.puttingOrganizations = false
          draft.putOrganizationsTopLevelError = undefined
          draft.isOrganizationBeingEdited = false
          return draft
        }

        case PUT_ORGANIZATION_FAILURE: {
          draft.puttingOrganizations = true
          draft.topLevelError = parseBackendErrors(
            action.payload,
          ).errorDetailMap?.name[0]
          draft.isOrganizationBeingEdited = true
          return draft
        }

        case ABANDON_EDIT_ORGANIZATION: {
          draft.puttingOrganizations = false
          draft.topLevelError = undefined
          draft.isOrganizationBeingEdited = false
          return draft
        }

        case PATCH_ORGANIZATIONS_SUCCESS: {
          draft.listResponses = {
            ...draft.listResponses,
            [action.payload.id]: action.payload,
          }
          draft.patchingOrganizations = false
          draft.patchOrganizationsTopLevelError = undefined
          return draft
        }

        case GET_ORGANIZATION_INVITE_SUCCESS: {
          draft.organizationInvites.listResponses = {
            ...draft.organizationInvites.listResponses,
            [action.payload.organizationInvite.id]:
              action.payload.organizationInvite,
          }
          return draft
        }

        case REMOVE_ORGANIZATION_INVITE: {
          delete draft.organizationInvites.listResponses[action.payload]
          return draft
        }

        case GET_ORGANIZATION_INVITES_SUCCESS:
        case GET_USER_ORGANIZATION_INVITES_SUCCESS: {
          draft.organizationInvites.listResponses =
            action.payload.organizationInvites.reduce(
              (acc, curr) => ({
                ...acc,
                [curr.id]: curr,
              }),
              {},
            )
          return draft
        }

        case GET_ORGANIZATION_INTEGRATIONS_SUCCESS: {
          draft.listResponses = {
            ...draft.listResponses,
            [action.orgId]: {
              ...draft.listResponses[action.orgId],
              integrations: action.payload.integrations,
            },
          }
          return draft
        }
      }
    }),
    reports: OrgExpenseReportsReducer(
      state.reports,
      action as GetOrgExpenseReportsActions,
    ),
  }
}

export default OrganizationsReducer
